/**
 * Route summary totals for the route detail panel.
 *
 * Cancelled stops (tasks cancelled in Core DB after the route was built) are
 * excluded from the count-based totals and service time, since the driver will
 * not service them. Distance, cost, and total duration are held to the
 * originally planned values (no geometry re-optimization).
 *
 * Driving time is reported geometry-pure (`total - service of ALL stops` = the
 * original planned driving), so it matches the per-stop drive legs and does NOT
 * change when a stop is cancelled. Consequence of the user's "keep total as
 * planned" choice: shown service + driving can be slightly less than the shown
 * total, by the cancelled stop's service minutes. That gap is intentional.
 */

import type { Route, RouteStop } from '../types';
import type { RouteStopWithStatus } from '../services/routes.api';

export interface RouteTotals {
  /** Number of non-cancelled stops. */
  activeStopCount: number;
  /** Sum of parcel counts across non-cancelled stops. */
  totalOrders: number;
  /** Count of non-cancelled pickup stops. */
  pickupCount: number;
  /** Count of non-cancelled RTO stops. */
  rtoCount: number;
  /** Sum of service minutes across non-cancelled stops. */
  totalServiceTimeMin: number;
  /** Geometry-pure driving minutes: stored total minus service of ALL stops (the original planned driving). Unaffected by cancelled stops; matches the per-stop drive legs. */
  totalDrivingTimeMin: number;
}

/**
 * A stop is treated as cancelled using its live Core DB status
 * (`stopsWithStatus`) when available, falling back to the persisted RouteStop
 * status (`'skipped'` is the cancelled mapping written by `sync-status`). Live
 * status is authoritative so a task un-cancelled in Core DB is not wrongly
 * excluded while the persisted status is still stale.
 */
export function computeRouteTotals(
  stops: RouteStop[],
  route: Pick<Route, 'total_duration_min'>,
  stopsWithStatus?: RouteStopWithStatus[],
): RouteTotals {
  const liveStatusByUid = new Map(
    (stopsWithStatus ?? []).map((s) => [s.task_uid, s.task_status]),
  );

  const isStopCancelled = (stop: RouteStop): boolean => {
    const live = liveStatusByUid.get(stop.task_uid);
    return live !== undefined ? live === 'cancelled' : stop.status === 'skipped';
  };

  const activeStops = stops.filter((stop) => !isStopCancelled(stop));

  const totalOrders = activeStops.reduce((sum, s) => sum + (s.parcel_count || 0), 0);
  const pickupCount = activeStops.filter((s) => s.type === 'pickup').length;
  const rtoCount = activeStops.filter((s) => s.type === 'rto').length;
  const totalServiceTimeMin = activeStops.reduce((sum, s) => sum + (s.service_time_min || 0), 0);

  const allStopsServiceMin = stops.reduce((sum, s) => sum + (s.service_time_min || 0), 0);

  return {
    activeStopCount: activeStops.length,
    totalOrders,
    pickupCount,
    rtoCount,
    totalServiceTimeMin,
    totalDrivingTimeMin: Math.max(0, route.total_duration_min - allStopsServiceMin),
  };
}
